import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BottomNavigation } from '../components/BottomNavigation';
import { 
  ArrowLeft,
  Star,
  Heart,
  Plus,
  Message,
  User,
  Location,
  Calendar,
  Edit,
  TickSquare,
  Delete,
  Notification
} from 'react-iconly';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, arrayUnion, arrayRemove, writeBatch } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { uploadShopCoverPhoto, uploadProductImage } from '../utils/imageUpload';
import { updateShopProfile } from '../utils/sellerApi';
import { trackStoreView, generateSessionId } from '../lib/analyticsService';
import { BackButton, SortableList } from '../components/ui';
import { useShopStore, useAppStore } from '../lib/stores';
import { useShopBySeller, useProductsBySeller, useAvailableProductsBySeller } from '../lib/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { productToasts, shopToasts, showError } from '../lib/utils/toast';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};
const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

// Feature flag for video feed experience
const USE_VIDEO_FEED = false;

// Enhanced video feed component using @dnd-kit
function ProductFeedView({ products, editMode, sellerId, shopName, onReorder, onProductClick, onVideoPlay, onVideoPause, onAddToCart, getCartQty, updateCartItem, removeCartItem, addStatus, videoRefs, currentPlayingIndex }) {
  // Only include products with videoUrl or imageUrl
  const feed = products.filter(p => p.videoUrl || p.imageUrl);

  if (!feed.length) {
    return <div style={{ color: '#fff', background: '#000', width: '100%', height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>No videos or images to display.</div>;
  }

  const handleReorder = (newOrder) => {
    console.log('[ProductFeedView] Products reordered:', newOrder);
    onReorder?.(newOrder);
  };

  const renderProductCard = (product, index) => (
    <div
      key={product.id}
      style={{
        position: 'relative',
        borderRadius: 0,
        overflow: 'hidden',
        background: '#fff',
        cursor: 'pointer',
        aspectRatio: '9/16',
        height: 'auto', // Let aspectRatio handle the height
        maxHeight: '320px',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        boxShadow: '0 1px 4px #0001',
      }}
      tabIndex={0}
      aria-label={`View details for ${product.name}`}
      onClick={() => {
        console.log('[ProductFeedView] Product card clicked:', product.id);
        onProductClick(product.id);
      }}
    >
      {/* Product Image/Video - Full Width */}
      <div style={{ 
        position: 'relative', 
        width: '100%',
        aspectRatio: '9/16',
        background: '#000',
        flex: 1
      }}>
        {/* Video Player */}
        <video
          ref={(el) => {
            if (el) {
              const productIndex = products.findIndex(p => p.id === product.id);
              if (productIndex !== -1) {
                // Direct assignment to prevent infinite loops
                videoRefs[productIndex] = el;
              }
            }
          }}
          src={product.videoUrl}
          poster={product.imageUrl}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: product.videoUrl ? 'block' : 'none'
          }}
          onPlay={() => onVideoPlay?.(index)}
          onPause={() => onVideoPause?.(index)}
          muted
          loop
          playsInline
        />
        
        {/* Image Fallback */}
        {!product.videoUrl && product.imageUrl && (
          <img
            src={product.imageUrl}
            alt={product.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        )}
        
        {/* Play Button Overlay for Videos */}
        {product.videoUrl && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 60,
              height: 60,
              background: 'rgba(0,0,0,0.7)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 2
            }}
            onClick={(e) => {
              e.stopPropagation();
              const video = videoRefs[index];
              if (video) {
                if (video.paused) {
                  video.play();
                } else {
                  video.pause();
                }
              }
            }}
          >
            <div style={{ color: '#fff', fontSize: 24 }}>▶</div>
          </div>
        )}
      </div>
      
      {/* Product Info Overlay */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
        color: '#fff',
        padding: '20px 16px 16px',
        zIndex: 1
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{product.name}</div>
        <div style={{ fontSize: 14, opacity: 0.9 }}>${product.price}</div>
      </div>
      
      {/* Drag Handle for Edit Mode */}
      {editMode && (
        <div
          className="drag-handle"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 32,
            height: 32,
            background: 'rgba(0,0,0,0.7)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            zIndex: 3,
            color: '#fff'
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          ⋮⋮
        </div>
      )}
    </div>
  );

  return (
    <SortableList
      items={feed}
      renderItem={renderProductCard}
      onReorder={handleReorder}
      disabled={!editMode}
      dragHandleClassName="drag-handle"
    />
  );
}

// Grid view component for products
function ProductGridView({ products, editMode, onProductClick, onAddToCart, getCartQty, updateCartItem, removeCartItem, addStatus }) {
  const [hoveredProduct, setHoveredProduct] = useState(null);

  const renderProductCard = (product) => (
    <div
      key={product.id}
      style={{
        background: NEUTRAL_WHITE,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        transform: hoveredProduct === product.id ? 'translateY(-4px)' : 'none',
        boxShadow: hoveredProduct === product.id ? '0 8px 24px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
      }}
      onMouseEnter={() => setHoveredProduct(product.id)}
      onMouseLeave={() => setHoveredProduct(null)}
      onClick={() => onProductClick(product.id)}
    >
      {/* Product Image */}
      <div style={{ position: 'relative', width: '100%', height: 200 }}>
        <img
          src={product.imageUrl || 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop'}
          alt={product.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        
        {/* Availability Badge */}
        {!product.available && (
          <div style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: ERROR_RED,
            color: NEUTRAL_WHITE,
            padding: '4px 8px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600,
            ...bodyFont
          }}>
            Out of Stock
          </div>
        )}
        
        {/* Add to Cart Button */}
        {product.available && (
          <button
            style={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              background: PRIMARY_BROWN,
              color: NEUTRAL_WHITE,
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 20,
              transition: 'background 0.2s'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(product);
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#B86A3F'}
            onMouseLeave={(e) => e.currentTarget.style.background = PRIMARY_BROWN}
          >
            <Plus set="bold" />
          </button>
        )}
      </div>
      
      {/* Product Info */}
      <div style={{ padding: 16 }}>
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          ...headingFont
        }}>
                                  <div style={{ 
                          fontSize: 12, 
                          fontWeight: 600, 
                          color: NEUTRAL_TEXT,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0
                        }}>
                          {product.name}
                        </div>
                        <div style={{ 
                          fontSize: 12, 
                          fontWeight: 700, 
                          color: '#000000',
                          marginLeft: 8
                        }}>
                          ${product.price}
                        </div>
        </div>
        

        

      </div>
    </div>
  );

  return (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
      gap: 20,
      padding: '0 20px'
    }}>
      {products.map(renderProductCard)}
    </div>
  );
}

export function StorePreview() {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { addToCart, getCartQty, updateCartItem, removeCartItem } = useCart();
  const { updateShop } = useShopStore();
  const { setCurrentProduct } = useAppStore();
  
  // State
  const [editMode, setEditMode] = useState(false);
  const [productsData, setProductsData] = useState([]);
  const [editShop, setEditShop] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasProductOrderChanges, setHasProductOrderChanges] = useState(false);
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [currentPlayingIndex, setCurrentPlayingIndex] = useState(null);
  const [videoRefs, setVideoRefs] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'feed'
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [draggedProductId, setDraggedProductId] = useState(null);
  const [dragOverProductId, setDragOverProductId] = useState(null);
  
  // Refs
  const profileInputRef = useRef();
  const coverInputRef = useRef();
  
  // Hooks
  const { data: shop, loading: shopLoading } = useShopBySeller(sellerId);
  const { data: products, loading: productsLoading } = useProductsBySeller(sellerId);
  const { data: availableProducts } = useAvailableProductsBySeller(sellerId);
  const queryClient = useQueryClient();

  // Initialize shop data
  useEffect(() => {
    if (shop) {
      setEditShop(shop);
    }
  }, [shop]);

  // Initialize products data
  useEffect(() => {
    console.log('[StorePreview] 🎯 Products useEffect triggered');
    console.log('[StorePreview] Current hasProductOrderChanges:', hasProductOrderChanges);
    console.log('[StorePreview] Current isSavingChanges:', isSavingChanges);
    console.log('[StorePreview] Current productsData length:', productsData.length);
    
    if (products && products.length > 0) {
      // Don't reinitialize if we have unsaved changes or are in edit mode
      if (hasProductOrderChanges || isSavingChanges) {
        console.log('[StorePreview] Skipping products initialization - have unsaved changes or saving in progress');
        return;
      }
      
      // Don't reinitialize if we just successfully saved changes and products already have displayOrder
      if (productsData.length > 0 && productsData.some(p => p.displayOrder !== undefined)) {
        console.log('[StorePreview] Skipping products initialization - products already have displayOrder values from previous save');
        return;
      }
      
      // Check if products need displayOrder initialization
      const productsNeedingOrder = products.filter(p => p.displayOrder === undefined);
      
      if (productsNeedingOrder.length > 0) {
        console.log('[StorePreview] Some products need displayOrder initialization:', productsNeedingOrder.map(p => p.name));
        
        // Only initialize if we don't have any existing displayOrder values
        const hasAnyDisplayOrder = products.some(p => p.displayOrder !== undefined);
        
        if (!hasAnyDisplayOrder) {
          console.log('[StorePreview] No existing displayOrder found, initializing all products');
          
          const productsWithOrder = products.map((product, index) => ({
            ...product,
            displayOrder: index
          }));
          
          setProductsData(productsWithOrder);
          
          // Save the initial order to the database
          const batch = writeBatch(db);
          productsWithOrder.forEach((product, index) => {
            console.log(`[StorePreview] Initializing product ${product.id} (${product.name}) to displayOrder: ${index}`);
            const productRef = doc(db, 'products', product.id);
            batch.update(productRef, { displayOrder: index });
          });
          
          batch.commit().then(() => {
            console.log('[StorePreview] Display order initialized successfully');
          }).catch(err => {
            console.error('[StorePreview] Error initializing display order:', err);
          });
        } else {
          console.log('[StorePreview] Some products have displayOrder, preserving existing order');
          // Sort products by their existing displayOrder
          const sortedProducts = [...products].sort((a, b) => {
            const orderA = a.displayOrder ?? 999999;
            const orderB = b.displayOrder ?? 999999;
            return orderA - orderB;
          });
          setProductsData(sortedProducts);
        }
      } else {
        console.log('[StorePreview] All products have displayOrder, preserving existing order');
        // Sort products by their existing displayOrder
        const sortedProducts = [...products].sort((a, b) => {
          const orderA = a.displayOrder ?? 999999;
          const orderB = b.displayOrder ?? 999999;
          return orderA - orderB;
        });
        setProductsData(sortedProducts);
      }
    }
  }, [products, hasProductOrderChanges, isSavingChanges]);

  // Track store view
  useEffect(() => {
    if (shop?.id && !editMode) {
      trackStoreView(shop.id, generateSessionId());
    }
  }, [shop?.id, editMode, currentUser?.uid]);

  // Initialize video refs
  useEffect(() => {
    if (productsData.length > 0) {
      setVideoRefs(new Array(productsData.length).fill(null));
    }
  }, [productsData, editMode, videoRefs.length]);

  // Auto-pause other videos when one starts playing
  useEffect(() => {
    if (currentPlayingIndex !== null && videoRefs.length > 0) {
      videoRefs.forEach((ref, index) => {
        if (index !== currentPlayingIndex && ref && !ref.paused) {
          ref.pause();
        }
      });
    }
  }, [currentPlayingIndex, productsData, editMode, videoRefs]);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = editMode && (
      JSON.stringify(editShop) !== JSON.stringify(shop) ||
      hasProductOrderChanges
    );
    setHasUnsavedChanges(hasChanges);
  }, [editMode, editShop, shop, hasProductOrderChanges]);

  // Handle shop updates
  const handleShopUpdate = async (field, value) => {
    if (!editMode || !shop?.id) return;
    
    setEditShop(prev => ({ ...prev, [field]: value }));
  };

  // Handle product reordering
  const handleProductReorder = async (newOrder) => {
    if (!editMode || !shop?.id) return;
    
    console.log('[StorePreview] 🎯 handleProductReorder called!');
    console.log('[StorePreview] Products reordered with @dnd-kit:', newOrder);
    console.log('[StorePreview] Current editMode:', editMode);
    console.log('[StorePreview] Current shop?.id:', shop?.id);
    
    // newOrder is the full array of products in new order
    const reorderedProducts = newOrder.map((product, index) => ({
      ...product,
      displayOrder: index
    }));
    
    console.log('[StorePreview] Setting new products state...');
    setProductsData(reorderedProducts);
    
    // Set flag to indicate product order has changed
    setHasProductOrderChanges(true);
    
    console.log('[StorePreview] Product order updated locally - will be saved when user clicks Save button');
  };

  // Simple HTML5 drag and drop handlers
  const handleDragStart = (e, productId) => {
    if (!editMode) return;
    console.log('[StorePreview] Drag allowed - starting drag for product:', productId);
    setIsDragging(true);
    setDraggedProductId(productId);
    e.dataTransfer.effectAllowed = 'move';
    
    // Set clean product ID in data transfer - use multiple formats for compatibility
    e.dataTransfer.setData('text/plain', productId);
    e.dataTransfer.setData('text', productId);
    
    console.log('[StorePreview] Data transfer set with product ID:', productId);
  };

  const handleDragOver = (e, productId) => {
    if (!editMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (productId !== draggedProductId) {
      setDragOverProductId(productId);
    }
  };

  const handleDragLeave = (e) => {
    if (!editMode) return;
    setDragOverProductId(null);
  };

  const handleDrop = async (e, targetProductId) => {
    console.log('[StorePreview] Drop called for target product:', targetProductId);
    console.log('[StorePreview] Edit mode:', editMode);
    console.log('[StorePreview] Current products before reorder:', productsData.map(p => ({ id: p.id, name: p.name })));
    
    if (!editMode) return;
    e.preventDefault();
    
    const draggedId = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text');
    console.log('[StorePreview] Dragged ID from data transfer:', draggedId);
    
    if (!draggedId) {
      console.log('[StorePreview] No dragged ID found in data transfer');
      setDragOverProductId(null);
      setIsDragging(false);
      setDraggedProductId(null);
      return;
    }

    if (draggedId === targetProductId) {
      console.log('[StorePreview] Same product dropped on itself, no reordering needed');
      setDragOverProductId(null);
      return;
    }

    const draggedIndex = productsData.findIndex(p => p.id === draggedId);
    const targetIndex = productsData.findIndex(p => p.id === targetProductId);
    
    console.log('[StorePreview] Product ID matching debug:', {
      draggedId,
      targetProductId,
      productsIds: productsData.map(p => p.id),
      draggedIndex,
      targetIndex,
      products: productsData.map(p => ({ id: p.id, name: p.name, type: typeof p.id }))
    });
    
    if (draggedIndex === -1 || targetIndex === -1) {
      console.log('[StorePreview] Invalid indices:', { draggedIndex, targetIndex });
      console.log('[StorePreview] Available product IDs:', productsData.map(p => p.id));
      console.log('[StorePreview] Dragged ID not found in products array');
      setDragOverProductId(null);
      setIsDragging(false);
      setDraggedProductId(null);
      return;
    }

    console.log('[StorePreview] Reordering products:', { 
      draggedIndex, 
      targetIndex, 
      draggedId, 
      targetProductId,
      draggedProductName: productsData[draggedIndex]?.name,
      targetProductName: productsData[targetIndex]?.name
    });

    // Create new products array with reordered items
    const newProducts = [...productsData];
    const [draggedProduct] = newProducts.splice(draggedIndex, 1);
    newProducts.splice(targetIndex, 0, draggedProduct);
    
    // Update displayOrder for all products
    const productsWithNewOrder = newProducts.map((product, index) => ({
      ...product,
      displayOrder: index
    }));
    
    console.log('[StorePreview] 🎯 DRAG DROP DEBUG:');
    console.log('[StorePreview] Original products order:', productsData.map((p, i) => ({ name: p.name, index: i, displayOrder: p.displayOrder })));
    console.log('[StorePreview] Dragged product:', { name: draggedProduct.name, fromIndex: draggedIndex, toIndex: targetIndex });
    console.log('[StorePreview] New products order:', newProducts.map((p, i) => ({ name: p.name, index: i, displayOrder: p.displayOrder })));
    console.log('[StorePreview] Products with updated displayOrder:', productsWithNewOrder.map(p => ({ name: p.name, displayOrder: p.displayOrder })));
    
    console.log('[StorePreview] New products order:', newProducts.map(p => ({ id: p.id, name: p.name })));
    
    console.log('[StorePreview] Setting new products state...');
    setProductsData(productsWithNewOrder);
    
    // Set flag to indicate product order has changed
    setHasProductOrderChanges(true);
    
    // Reset drag states
    setDragOverProductId(null);
    setIsDragging(false);
    setDraggedProductId(null);
    
    console.log('[StorePreview] Product order updated locally - will be saved when user clicks Save button');
  };





  // Handle product click
  const handleProductClick = (productId) => {
    console.log('[StorePreview] Product clicked:', productId);
    console.log('[StorePreview] Edit mode:', editMode);
    console.log('[StorePreview] Shop ID:', shop?.id);
    console.log('[StorePreview] Shop object:', shop);
    console.log('[StorePreview] sellerId from params:', sellerId);
    
    if (editMode) {
      // In edit mode, navigate to product edit page
      // console.log('[StorePreview] Navigation blocked - edit mode active');
      return;
    }
    
    if (!shop?.id) {
      console.log('[StorePreview] Navigation blocked - no shop ID');
      return;
    }
    
    // Create product order string to preserve the order from storefront
    const productOrder = productsData
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map(p => p.id)
      .join(',');
    
    const url = `/product/${productId}?storeId=${shop.id}&productOrder=${productOrder}`;
    console.log('[StorePreview] Navigating to:', url);
    navigate(url);
  };

  // Handle video play/pause
  const handleVideoPlay = (index) => {
    if (editMode) return; // Don't play videos when in edit mode
    setCurrentPlayingIndex(index);
  };

  const handleVideoPause = (index) => {
    if (editMode) return; // Don't pause videos when in edit mode
    setCurrentPlayingIndex(null);
  };

  // Handle add to cart
  const handleAddToCart = (product) => {
    addToCart(product);
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!editShop || !shop?.id) return;
    
    try {
      setIsSavingChanges(true);
      console.log('[StorePreview] Saving shop changes...');
      
      // Save shop profile changes
      if (JSON.stringify(editShop) !== JSON.stringify(shop)) {
        await updateShopProfile(shop.id, editShop);
        console.log('[StorePreview] Shop profile updated successfully');
      }
      
      // Save product reordering changes
      if (hasProductOrderChanges) {
        console.log('[StorePreview] Saving product reordering changes...');
        
        // Create the new order with displayOrder values
        const reorderedProducts = productsData.map((product, index) => ({
          ...product,
          displayOrder: index
        }));
        
        console.log('[StorePreview] 🎯 SAVE DEBUG:');
        console.log('[StorePreview] Current productsData:', productsData.map(p => ({ name: p.name, displayOrder: p.displayOrder })));
        console.log('[StorePreview] Reordered products for save:', reorderedProducts.map(p => ({ name: p.name, displayOrder: p.displayOrder })));
        
        console.log('[StorePreview] New order:', reorderedProducts.map(p => ({ name: p.name, displayOrder: p.displayOrder })));
        
        // Update each product in the database
        for (let i = 0; i < reorderedProducts.length; i++) {
          const product = reorderedProducts[i];
          const productRef = doc(db, 'products', product.id);
          
          console.log(`[StorePreview] Updating ${product.name} to displayOrder: ${product.displayOrder}`);
          
          try {
            await updateDoc(productRef, { displayOrder: product.displayOrder });
            console.log(`[StorePreview] ✅ ${product.name} updated successfully`);
          } catch (updateError) {
            console.error(`[StorePreview] ❌ Failed to update ${product.name}:`, updateError);
            throw updateError;
          }
        }
        
        console.log('[StorePreview] ✅ All products updated successfully');
      }
      
      // Invalidate cache to ensure fresh data
      console.log('[StorePreview] Invalidating cache...');
      try {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['products', 'seller', sellerId]
          }),
          queryClient.invalidateQueries({
            queryKey: ['products', 'seller', sellerId, 'available']
          })
        ]);
        console.log('[StorePreview] Cache invalidated successfully');
      } catch (cacheError) {
        console.error('[StorePreview] Cache invalidation failed:', cacheError);
      }
      
      console.log('[StorePreview] Showing success message and exiting edit mode');
      shopToasts.saved();
      
      // Exit edit mode
      setEditMode(false);
      
      // Reset saving flag
      setIsSavingChanges(false);
      setHasUnsavedChanges(false);
      setHasProductOrderChanges(false);
      
    } catch (error) {
      console.error('[StorePreview] Error saving changes:', error);
      setIsSavingChanges(false);
      // Show error message to user
      alert('Failed to save changes. Please try again.');
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditShop(shop);
    setProductsData(products);
    setEditMode(false);
    setHasUnsavedChanges(false);
    setHasProductOrderChanges(false);
  };

  // Handle profile image upload
  const handleProfileImageUpload = async (file) => {
    if (!shop?.id) return;
    
    try {
      const imageUrl = await uploadShopCoverPhoto(file, shop.id, 'profile');
      handleShopUpdate('profileImage', imageUrl);
    } catch (error) {
      showError('Failed to upload profile image');
    }
  };

  // Handle cover image upload
  const handleCoverImageUpload = async (file) => {
    if (!shop?.id) return;
    
    try {
      const imageUrl = await uploadShopCoverPhoto(file, shop.id, 'cover');
      handleShopUpdate('coverPhotoURL', imageUrl);
    } catch (error) {
      showError('Failed to upload cover image');
    }
  };

  if (shopLoading || productsLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: NEUTRAL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontSize: 18, color: ACCENT_GRAY }}>Loading store...</div>
      </div>
    );
  }

  if (!shop || !products) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: NEUTRAL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontSize: 18, color: ACCENT_GRAY }}>Store not found</div>
      </div>
    );
  }

  // Sort products by display order - use productsData for edit mode, availableProducts for customer view
  const sortedProducts = editMode 
    ? [...productsData].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    : [...(availableProducts || products || [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG, paddingTop: '80px' }}>
      {/* Header - Exact Seller Hub Style */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        padding: '16px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* Left side - Title and Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => {}} // TODO: Add toggleQuickActions functionality
              style={{
                background: 'transparent',
                border: 'none',
                padding: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = NEUTRAL_BG;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <div style={{
                width: 16,
                height: 12,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}>
                <div style={{
                  width: '100%',
                  height: 2,
                  background: NEUTRAL_TEXT,
                  borderRadius: 1,
                  transition: 'all 0.2s ease'
                }} />
                <div style={{
                  width: '100%',
                  height: 2,
                  background: NEUTRAL_TEXT,
                  borderRadius: 1,
                  transition: 'all 0.2s ease'
                }} />
                <div style={{
                  width: '100%',
                  height: 2,
                  background: NEUTRAL_TEXT,
                  borderRadius: 1,
                  transition: 'all 0.2s ease'
                }} />
              </div>
            </button>
            <h1 style={{
              fontSize: 16,
              fontWeight: 700,
              color: NEUTRAL_TEXT,
              margin: 0,
              ...headingFont
            }}>
              Store Preview
            </h1>
          </div>

          {/* Desktop Navigation */}
          {window.innerWidth > 768 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}>
              <button
                onClick={() => navigate('/seller-hub')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: ACCENT_GRAY,
                  cursor: 'pointer',
                  borderRadius: 20,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  ...bodyFont
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = NEUTRAL_BG;
                  e.currentTarget.style.color = NEUTRAL_TEXT;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = ACCENT_GRAY;
                }}
              >
                Dashboard
              </button>
              
              <button
                onClick={() => navigate('/all-products')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: ACCENT_GRAY,
                  cursor: 'pointer',
                  borderRadius: 20,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  ...bodyFont
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = NEUTRAL_BG;
                  e.currentTarget.style.color = NEUTRAL_TEXT;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = ACCENT_GRAY;
                }}
              >
                Products
              </button>
              
              <button
                onClick={() => navigate('/orders')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: ACCENT_GRAY,
                  cursor: 'pointer',
                  borderRadius: 20,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  ...bodyFont
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = NEUTRAL_BG;
                  e.currentTarget.style.color = NEUTRAL_TEXT;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = ACCENT_GRAY;
                }}
              >
                Orders
              </button>
              
              <button
                onClick={() => navigate('/seller-hub/messages')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: ACCENT_GRAY,
                  cursor: 'pointer',
                  borderRadius: 20,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  ...bodyFont
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = NEUTRAL_BG;
                  e.currentTarget.style.color = NEUTRAL_TEXT;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = ACCENT_GRAY;
                }}
              >
                Messages
              </button>
              
              <button
                onClick={() => navigate(`/store-settings/${currentUser?.uid}`)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: ACCENT_GRAY,
                  cursor: 'pointer',
                  borderRadius: 20,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  ...bodyFont
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = NEUTRAL_BG;
                  e.currentTarget.style.color = NEUTRAL_TEXT;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = ACCENT_GRAY;
                }}
              >
                Settings
              </button>
            </div>
          )}
        </div>

        {/* Right side - Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            style={{
              background: 'rgba(255,255,255,0.9)',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              position: 'relative'
            }}
            onClick={() => navigate(`/store-preview/${currentUser?.uid}`)}
            aria-label="Go to Store Preview"
          >
            <TickSquare set="regular" size={20} primaryColor={NEUTRAL_TEXT} />
          </button>
          <button
            style={{
              background: 'rgba(255,255,255,0.9)',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              position: 'relative'
            }}
          >
            <Notification set="regular" size={20} primaryColor={NEUTRAL_TEXT} />
          </button>
        </div>
      </header>



      {/* Edit Store Button - Below Header */}
      {!editMode && (
        <div style={{
          position: 'sticky',
          top: '80px',
          zIndex: 10,
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 24px',
          marginTop: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          paddingBottom: '16px'
        }}>
          <button
            onClick={() => setEditMode(true)}
            style={{
              background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
              border: 'none',
              color: NEUTRAL_WHITE,
              padding: '8px 12px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              boxShadow: '0 1px 3px 0 rgba(244, 162, 97, 0.3), 0 1px 2px 0 rgba(244, 162, 97, 0.2)',
              transition: 'all 0.2s',
              ...bodyFont
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 6px -1px rgba(201, 123, 83, 0.3), 0 2px 4px -1px rgba(201, 123, 83, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 1px 3px 0 rgba(201, 123, 83, 0.3), 0 1px 2px 0 rgba(201, 123, 83, 0.2)';
            }}
          >
            Edit Store
          </button>
        </div>
      )}

      {/* Edit Mode Action Buttons */}
      {editMode && (
        <div style={{
          position: 'sticky',
          top: '80px',
          zIndex: 10,
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 24px',
          marginTop: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          paddingBottom: '16px'
        }}>
          <button
            onClick={handleCancelEdit}
            style={{
              background: 'none',
              border: '1px solid #D1D5DB',
              color: '#6B7280',
              padding: '8px 12px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              transition: 'all 0.2s',
              ...bodyFont
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#9CA3AF';
              e.target.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#D1D5DB';
              e.target.style.color = '#6B7280';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={!hasUnsavedChanges}
            style={{
              background: hasUnsavedChanges ? 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)' : '#D1D5DB',
              border: 'none',
              color: hasUnsavedChanges ? NEUTRAL_WHITE : '#9CA3AF',
              padding: '8px 12px',
              borderRadius: '8px',
              cursor: hasUnsavedChanges ? 'pointer' : 'not-allowed',
              fontSize: '12px',
              fontWeight: '600',
              transition: 'all 0.2s',
              ...bodyFont
            }}
            onMouseEnter={(e) => {
              if (hasUnsavedChanges) {
                e.target.style.background = 'linear-gradient(135deg, #e76f51 0%, #d65a3f 50%, #e76f51 100%)';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 6px -1px rgba(244, 162, 97, 0.3), 0 2px 4px -1px rgba(244, 162, 97, 0.2)';
              }
            }}
            onMouseLeave={(e) => {
              if (hasUnsavedChanges) {
                e.target.style.background = 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 1px 3px 0 rgba(244, 162, 97, 0.3), 0 1px 2px 0 rgba(244, 162, 97, 0.2)';
              }
            }}
          >
            Save
          </button>
        </div>
      )}

      {/* Shop Header Section */}
      <section style={{
        maxWidth: 480,
        margin: '32px auto',
        background: NEUTRAL_WHITE,
        borderRadius: 20,
        boxShadow: '0 2px 8px #0001',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 24
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%',
          gap: 24,
          justifyContent: 'center',
          marginBottom: 16
        }}>
          {/* Logo as round image */}
          <div style={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            background: '#E5C7B2',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative'
          }}>
            <img
              src={editMode ? (editShop?.profileImage || undefined) : (shop?.profileImage || undefined)}
              alt={`${shop?.name} logo`}
              width={88}
              height={88}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', cursor: editMode ? 'pointer' : 'default' }}
              onClick={editMode ? () => profileInputRef.current && profileInputRef.current.click() : undefined}
            />
            {editMode && (
              <input
                ref={profileInputRef}
                id="profile-image-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleProfileImageUpload(file);
                }}
              />
            )}
            {editMode && (
              <label style={{ position: 'absolute', bottom: 8, right: 8, padding: 0, cursor: 'pointer', background: 'transparent', borderRadius: 0, boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }} htmlFor="profile-image-input">
                <Edit set="light" primaryColor="#fff" size={24} />
              </label>
            )}
          </div>
          {/* Shop Name and Description */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', flex: 1 }}>
            <h1 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, margin: 0, textAlign: 'left' }}>{shop?.name}</h1>
            {editMode ? (
              <textarea
                value={editShop?.description || ''}
                onChange={(e) => handleShopUpdate('description', e.target.value)}
                style={{ color: ACCENT_GRAY, fontSize: 12, marginTop: 6, textAlign: 'left', ...bodyFont, width: '100%', minHeight: 40, borderRadius: 8, border: '1px solid #eee', padding: 8 }}
                placeholder="Shop description"
              />
            ) : (
              <div style={{ color: ACCENT_GRAY, fontSize: 12, marginTop: 6, textAlign: 'left', ...bodyFont }}>{shop?.description}</div>
            )}
          </div>
        </div>
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 16, marginTop: 8, width: '100%', justifyContent: 'center' }}>
          <button
            onClick={() => {}} // TODO: Implement favorite functionality
            aria-label="Favorite shop"
            style={{
              background: 'transparent',
              color: PRIMARY_BROWN,
              border: `2px solid ${PRIMARY_BROWN}`,
              borderRadius: 12,
              padding: '10px 32px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              ...bodyFont,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 1px 4px #0001',
              transition: 'all 0.2s ease'
            }}
          >
            <Heart set="light" primaryColor={PRIMARY_BROWN} size={20} />
            Favorite
          </button>
          <button
            onClick={() => {}} // TODO: Implement message functionality
            aria-label="Message shop owner"
            style={{
              background: 'transparent',
              color: PRIMARY_BROWN,
              border: `2px solid ${PRIMARY_BROWN}`,
              borderRadius: 12,
              padding: '10px 32px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              ...bodyFont,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 1px 4px #0001',
              transition: 'all 0.2s ease'
            }}
          >
            <Message set="light" primaryColor={PRIMARY_BROWN} size={20} />
            Message
          </button>
        </div>
      </section>

      {/* Metric Cards */}
      <section style={{
        maxWidth: 480,
        margin: '32px auto',
        background: NEUTRAL_WHITE,
        borderRadius: 20,
        boxShadow: '0 2px 8px #0001',
        padding: 24
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 16
        }}>
          {/* Favorites Card */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '20px 16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              lineHeight: 1
            }}>
              {shop?.favoritesCount || 0}
            </div>
            <div style={{
              fontSize: '12px',
              color: ACCENT_GRAY,
              fontWeight: '500'
            }}>
              Favorites
            </div>
          </div>

          {/* Rating Card */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '20px 16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              lineHeight: 1
            }}>
              {shop?.rating ? (
                <>
                  {shop.rating}
                  <span style={{
                    fontSize: '18px',
                    color: ACCENT_GRAY,
                    fontWeight: '400'
                  }}>/5</span>
                </>
              ) : (
                '--'
              )}
            </div>
            <div style={{
              fontSize: '12px',
              color: ACCENT_GRAY,
              fontWeight: '500'
            }}>
              Stars
            </div>
          </div>

          {/* Orders Card */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '20px 16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              lineHeight: 1
            }}>
              {shop?.completedOrdersCount || 0}
            </div>
            <div style={{
              fontSize: '12px',
              color: ACCENT_GRAY,
              fontWeight: '500'
            }}>
              Orders
            </div>
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section style={{
        maxWidth: 480,
        margin: '32px auto',
        background: NEUTRAL_WHITE,
        borderRadius: 20,
        boxShadow: '0 2px 8px #0001',
        padding: 24
      }}>
        {shopLoading || productsLoading ? (
          <div style={{ color: ACCENT_GRAY, fontSize: 18, textAlign: 'center', ...bodyFont }}>
            {shopLoading ? 'Loading shop...' : 'Loading products...'}
          </div>
        ) : (
          (() => {
            // Filter products based on edit mode and availability
            const filteredProducts = editMode
              ? sortedProducts.filter(p => {
                  // For edit mode, show only active products (same as customer view)
                  const isActive = p.published === true || p.status === true;
                  
                  // Check if product has unlimited stock
                  if (p.unlimited === true) return isActive;
                  
                  // Check regular stock field
                  if (p.stock > 0) return isActive;
                  
                  // Check new inventory schema for any available dates
                  if (p.inventory && p.inventory.scheduled && Array.isArray(p.inventory.scheduled)) {
                    const hasAvailableStock = p.inventory.scheduled.some(row => 
                      row.enabled && row.available_total > 0
                    );
                    if (hasAvailableStock) return isActive;
                  }
                  
                  // Check legacy daily inventory for any available dates
                  if (p.dailyInventory && typeof p.dailyInventory === 'object') {
                    const hasAvailableStock = Object.values(p.dailyInventory).some(stock => {
                      const numStock = Number(stock);
                      return !isNaN(numStock) && numStock > 0;
                    });
                    if (hasAvailableStock) return isActive;
                  }
                  
                  // Check daily stock field (legacy)
                  if (p.dailyStock && typeof p.dailyStock === 'object') {
                    const hasAvailableStock = Object.values(p.dailyStock).some(stock => {
                      const numStock = Number(stock);
                      return !isNaN(numStock) && numStock > 0;
                    });
                    if (hasAvailableStock) return isActive;
                  }
                  
                  return false; // No available stock found
                })
              : sortedProducts.filter(p => {
                  // For customer view, check if product is active AND has available stock
                  const isActive = p.published === true || p.status === true;
                  
                  // Check if product has unlimited stock
                  if (p.unlimited === true) return isActive;
                  
                  // Check regular stock field
                  if (p.stock > 0) return isActive;
                  
                  // Check new inventory schema for any available dates
                  if (p.inventory && p.inventory.scheduled && Array.isArray(p.inventory.scheduled)) {
                    const hasAvailableStock = p.inventory.scheduled.some(row => 
                      row.enabled && row.available_total > 0
                    );
                    if (hasAvailableStock) return isActive;
                  }
                  
                  // Check legacy daily inventory for any available dates
                  if (p.dailyInventory && typeof p.dailyInventory === 'object') {
                    const hasAvailableStock = Object.values(p.dailyInventory).some(stock => {
                      const numStock = Number(stock);
                      return !isNaN(numStock) && numStock > 0;
                    });
                    if (hasAvailableStock) return isActive;
                  }
                  
                  // Check daily stock field (legacy)
                  if (p.dailyStock && typeof p.dailyStock === 'object') {
                    const hasAvailableStock = Object.values(p.dailyStock).some(stock => {
                      const numStock = Number(stock);
                      return !isNaN(numStock) && numStock > 0;
                    });
                    if (hasAvailableStock) return isActive;
                  }
                  
                  return false; // No available stock found
                });

            // If no products after filtering, show a message
            if (filteredProducts.length === 0) {
              return (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: ACCENT_GRAY,
                  fontSize: 16,
                  ...bodyFont
                }}>
                  {sortedProducts.length === 0 
                    ? 'No products available yet.' 
                    : 'All products are currently unavailable.'
                  }
                </div>
              );
            }

                        return editMode ? (
              // Edit mode with simple HTML5 drag and drop
              (() => {
                console.log('[StorePreview] 🎯 Rendering edit mode with simple drag and drop');
                console.log('[StorePreview] Filtered products count:', filteredProducts.length);
                console.log('[StorePreview] 🎯 Filtered products:', filteredProducts.map(p => ({ id: p.id, name: p.name })));
                return (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 20,
                    maxWidth: '100%',
                    margin: '0 auto'
                  }}>
                    {filteredProducts.map((product, index) => (
                      <div
                        key={product.id}
                        draggable={editMode}
                        onDragStart={(e) => handleDragStart(e, product.id)}
                        onDragOver={(e) => handleDragOver(e, product.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, product.id)}
                        style={{
                          background: NEUTRAL_WHITE,
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          cursor: editMode ? 'grab' : 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                          height: 'fit-content',
                          opacity: draggedProductId === product.id ? 0.5 : 1,
                          transform: draggedProductId === product.id ? 'scale(0.95)' : 'scale(1)',
                          border: dragOverProductId === product.id ? '2px dashed #C97B53' : 'none'
                        }}
                        onClick={(e) => {
                          // Prevent click when dragging or clicking on drag handle
                          if (e.target.closest('.drag-handle')) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                          }
                          handleProductClick(product.id);
                        }}
                        tabIndex={0}
                        aria-label={`View details for ${product.name}`}
                      >
                        {/* Drag Handle for Edit Mode */}
                        {editMode && (
                          <div
                            className="drag-handle"
                            style={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              width: 32,
                              height: 32,
                              background: 'rgba(0,0,0,0.7)',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'grab',
                              zIndex: 3,
                              color: '#fff',
                              fontSize: 16,
                              fontWeight: 'bold',
                              userSelect: 'none',
                              touchAction: 'none'
                            }}
                          >
                            ⋮⋮
                          </div>
                        )}

                    
                    {/* Product Image Container */}
                    <div style={{ 
                      position: 'relative', 
                      width: '100%',
                      aspectRatio: '9/16',
                      background: '#f5f5f5',
                      maxHeight: '320px'
                    }}>
                      {/* Product Image */}
                      <img
                        src={product.imageUrl || 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop'}
                        alt={product.name}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          aspectRatio: '9/16'
                        }}
                      />
                      
                      {/* Availability Badge */}
                      {!product.available && (
                        <div style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          background: ERROR_RED,
                          color: NEUTRAL_WHITE,
                          padding: '4px 8px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          ...bodyFont
                        }}>
                          Out of Stock
                        </div>
                      )}
                    </div>
                    
                    {/* Product Info */}
                    <div style={{ padding: 16, flex: 1 }}>
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 8,
                        ...headingFont
                      }}>
                        <div style={{ 
                          fontSize: 12, 
                          fontWeight: 600, 
                          color: NEUTRAL_TEXT,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0
                        }}>
                          {product.name}
                        </div>
                        <div style={{ 
                          fontSize: 12, 
                          fontWeight: 700, 
                          color: '#000000',
                          marginLeft: 8
                        }}>
                          ${product.price}
                        </div>
                      </div>
                    </div>
                                      </div>
                  ))}
                </div>
                );
              })()
            ) : (
              // Non-edit mode without drag and drop
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 20,
                maxWidth: '100%',
                margin: '0 auto'
              }}>
                {filteredProducts.map((product, index) => (
                  <div
                    key={product.id}
                    style={{
                      background: NEUTRAL_WHITE,
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onClick={() => handleProductClick(product.id)}
                    tabIndex={0}
                    aria-label={`View details for ${product.name}`}
                  >
                    {/* Product Image Container */}
                    <div style={{ 
                      position: 'relative', 
                      width: '100%',
                      aspectRatio: '9/16',
                      background: '#f5f5f5',
                      maxHeight: '320px'
                    }}>
                      {/* Product Image */}
                      <img
                        src={product.imageUrl || 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop'}
                        alt={product.name}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          aspectRatio: '9/16'
                        }}
                      />
                      
                      {/* Availability Badge */}
                      {!product.available && (
                        <div style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          background: ERROR_RED,
                          color: NEUTRAL_WHITE,
                          padding: '4px 8px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          ...bodyFont
                        }}>
                          Out of Stock
                        </div>
                      )}
                    </div>
                    
                    {/* Product Info */}
                    <div style={{ padding: 16, flex: 1 }}>
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 8,
                        ...headingFont
                      }}>
                        <div style={{ 
                          fontSize: 12, 
                          fontWeight: 600, 
                          color: NEUTRAL_TEXT,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0
                        }}>
                          {product.name}
                        </div>
                        <div style={{ 
                          fontSize: 12, 
                          fontWeight: 700, 
                          color: '#000000',
                          marginLeft: 8
                        }}>
                          ${product.price}
                        </div>
                      </div>
                      

                    </div>
                  </div>
                ))}
              </div>
            );
          })()
        )}
      </section>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );

}

export default StorePreview;
